import React from 'react';
import { Row, Col, Card } from 'react-bootstrap';
import { Link } from 'react-router-dom';

import '../CommunityGuidelines.css';

/**
 * GuidelinesContent Component
 * Reusable guidelines content that can be rendered standalone or within tabs
 */
function GuidelinesContent() {
    return (
        <Row className="g-4">
            {/* Community Guidelines - Intro */}
            <Col md={6}>
                <Card className="guidelines-card h-100">
                    <Card.Body className="p-4">
                        <h5 className="guidelines-title mb-3">
                            Community Guidelines
                        </h5>
                        <p className="guidelines-text mb-0">
                            Welcome to Communities of Interest! Our goal is to
                            create a space where everyone can learn,
                            collaborate, and support each other. These
                            guidelines help us maintain a safe, respectful, and
                            productive environment for all members.
                        </p>
                    </Card.Body>
                </Card>
            </Col>

            {/* Communication and Respect */}
            <Col md={6}>
                <Card className="guidelines-card h-100">
                    <Card.Body className="p-4">
                        <h5 className="guidelines-title mb-3">
                            Communication and Respect
                        </h5>
                        <ul className="guidelines-list mb-0">
                            <li>
                                Treat everyone with kindness and
                                professionalism. Listen actively, consider
                                different perspectives, and avoid personal
                                attacks or hostile behavior.
                            </li>
                            <li>
                                Hate speech or language targeting individuals or
                                groups based on identity, background, or beliefs
                                is strictly prohibited.
                            </li>
                        </ul>
                    </Card.Body>
                </Card>
            </Col>

            {/* Content Quality */}
            <Col md={6}>
                <Card className="guidelines-card h-100">
                    <Card.Body className="p-4">
                        <h5 className="guidelines-title mb-3">
                            Content Quality
                        </h5>
                        <ul className="guidelines-list mb-0">
                            <li>
                                Each community focuses on a specific
                                sustainability theme. Keep discussions relevant
                                and constructive to help others find useful
                                information.
                            </li>
                            <li>
                                Please share reliable sources, data, or
                                experiences that contribute positively to
                                learning. Avoid posting misleading or unverified
                                content.
                            </li>
                        </ul>
                    </Card.Body>
                </Card>
            </Col>

            {/* Community Standards */}
            <Col md={6}>
                <Card className="guidelines-card h-100">
                    <Card.Body className="p-4">
                        <h5 className="guidelines-title mb-3">
                            Community Standards
                        </h5>
                        <ul className="guidelines-list mb-0">
                            <li>
                                Spam, ads, repeated messages, or unsolicited
                                promotional content are not allowed. This helps
                                keep communities clean and focused.
                            </li>
                            <li>
                                If you see harmful content, please report it. Do
                                not misuse the report feature to target others
                                unfairly.
                            </li>
                        </ul>
                    </Card.Body>
                </Card>
            </Col>

            {/* Zero-Tolerance Policy */}
            <Col md={6}>
                <Card className="guidelines-card h-100">
                    <Card.Body className="p-4">
                        <h5 className="guidelines-title mb-3">
                            Zero-Tolerance Policy
                        </h5>
                        <p className="guidelines-text mb-0">
                            We maintain a zero-tolerance policy for harassment,
                            cyberbullying, threats, or abusive behavior. Any
                            violations will result in immediate content removal
                            and may lead to account suspension or permanent ban.
                        </p>
                    </Card.Body>
                </Card>
            </Col>

            {/* How to Report Content */}
            <Col md={6}>
                <Card className="guidelines-card h-100">
                    <Card.Body className="p-4">
                        <h5 className="guidelines-title mb-3">
                            How to Report Content
                        </h5>
                        <p className="guidelines-text mb-2">
                            If you encounter content that violates these
                            guidelines, please use the Report feature:
                        </p>
                        <ol className="guidelines-numbered-list mb-0">
                            <li>
                                Click the three dots menu on any post or comment
                            </li>
                            <li>
                                Select &quot;Report&quot; and choose the
                                violation type
                            </li>
                            <li>Add any additional context (optional)</li>
                            <li>Submit your report</li>
                        </ol>
                    </Card.Body>
                </Card>
            </Col>

            {/* Content Takedown Process */}
            <Col md={6}>
                <Card className="guidelines-card h-100">
                    <Card.Body className="p-4">
                        <h5 className="guidelines-title mb-3">
                            Content Takedown Process
                        </h5>
                        <p className="guidelines-text mb-2">
                            If you believe content on our platform infringes on
                            your copyright, you can submit a DMCA takedown
                            request. The process requires:
                        </p>
                        <ul className="guidelines-list mb-3">
                            <li>Identification of the copyrighted work</li>
                            <li>Location of the infringing content (URL)</li>
                            <li>Your contact information</li>
                            <li>A statement of good faith and accuracy</li>
                        </ul>
                        <Link
                            to="/policies/takedown"
                            className="btn btn-primary btn-sm btn-primary-outline-hover"
                        >
                            View Full Takedown Policy
                        </Link>
                    </Card.Body>
                </Card>
            </Col>

            {/* Automated Moderation */}
            <Col md={6}>
                <Card className="guidelines-card h-100">
                    <Card.Body className="p-4">
                        <h5 className="guidelines-title mb-3">
                            Automated Moderation
                        </h5>
                        <p className="guidelines-text mb-2">
                            All posts in Communities of Interest are
                            automatically reviewed by an AI-powered moderation
                            system before they are published. When you submit a
                            post, one of three outcomes may occur:
                        </p>
                        <ul className="guidelines-list mb-3">
                            <li>
                                <strong>Published</strong> &mdash; Your post
                                passes the automated review and appears
                                immediately.
                            </li>
                            <li>
                                <strong>Under Review</strong> &mdash; Your post
                                is held for human moderator review before
                                publishing.
                            </li>
                            <li>
                                <strong>Blocked</strong> &mdash; Your post was
                                flagged and not published. A human moderator
                                will review the content before any final action
                                is taken.
                            </li>
                        </ul>
                        <p className="guidelines-text mb-3">
                            Automated tools assist moderation but do not make
                            final decisions independently. If you believe your
                            post was blocked in error, you have the right to
                            appeal.
                        </p>
                        <Link
                            to="/policies/moderation"
                            className="btn btn-primary btn-sm btn-primary-outline-hover"
                        >
                            View Moderation Policy &amp; Appeal Process
                        </Link>
                    </Card.Body>
                </Card>
            </Col>
        </Row>
    );
}

export default GuidelinesContent;
